import { useState } from 'react';
import { Mail, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import { useSite } from '../context/SiteContext';
import { setPageMeta } from '../lib/shop';
import { useEffect } from 'react';

export default function Contact() {
  const { site } = useSite();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => { setPageMeta('Contact', 'Contact VQ Health — general, support and order enquiries.'); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success('Message sent — we reply within one working day.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) { toast.error(errMsg(err)); } finally { setSending(false); }
  };

  const channels = [
    { label: 'General', value: site.email_general || 'hello@vqhealth.co.uk', href: `mailto:${site.email_general || 'hello@vqhealth.co.uk'}`, icon: Mail, tid: 'contact-general' },
    { label: 'Support', value: site.email_support || 'support@vqhealth.co.uk', href: `mailto:${site.email_support || 'support@vqhealth.co.uk'}`, icon: Mail, tid: 'contact-support' },
    { label: 'Orders', value: site.email_orders || 'orders@vqhealth.co.uk', href: `mailto:${site.email_orders || 'orders@vqhealth.co.uk'}`, icon: Mail, tid: 'contact-orders' },
  ];
  if (site.whatsapp_url) channels.push({ label: 'WhatsApp', value: 'Chat with us', href: site.whatsapp_url, icon: MessageCircle, tid: 'contact-whatsapp' });
  if (site.telegram_url) channels.push({ label: 'Telegram', value: 'Join the channel', href: site.telegram_url, icon: Send, tid: 'contact-telegram' });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="contact-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Contact</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-10">Talk to VQ Health</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-3">
          {channels.map((c) => (
            <a key={c.label} href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" data-testid={c.tid}
              className="flex items-center gap-4 border hairline bg-white p-5 hover:border-[#0B0F12] transition-colors">
              <c.icon className="w-5 h-5" />
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">{c.label}</p>
                <p className="font-display font-bold uppercase text-lg">{c.value}</p>
              </div>
            </a>
          ))}
          <div className="border hairline bg-white p-5">
            <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Response time</p>
            <p className="text-sm text-[#575B60] mt-1">Emails are answered within one UK working day. For order queries, include your order number (VQ-####).</p>
          </div>
        </div>
        <form onSubmit={submit} className="border hairline bg-white p-6 space-y-4">
          <p className="font-display font-bold uppercase text-xl">Send a message</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="contact-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border hairline px-4 py-3 text-sm focus:outline-none focus:border-[#0B0F12]" />
            <input data-testid="contact-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border hairline px-4 py-3 text-sm focus:outline-none focus:border-[#0B0F12]" />
          </div>
          <input data-testid="contact-subject" placeholder="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="w-full border hairline px-4 py-3 text-sm focus:outline-none focus:border-[#0B0F12]" />
          <textarea data-testid="contact-message" required rows={5} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full border hairline px-4 py-3 text-sm focus:outline-none focus:border-[#0B0F12]" />
          <button data-testid="contact-submit" disabled={sending}
            className="w-full bg-[#0B0F12] text-white py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors disabled:opacity-50">
            {sending ? 'Sending…' : 'Send message'}
          </button>
        </form>
      </div>
    </div>
  );
}
